import { useState, useEffect } from 'react'
import QRCode from 'qrcode'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Checkbox,
  Skeleton,
  useToast,
} from '@lead-magnifier/ui'
import { UserPlus, Users, Trash2, Loader2, QrCode, Download, Copy, Check, ExternalLink } from 'lucide-react'
import {
  useEventExhibitors,
  useAvailableExhibitors,
  useBulkAssignExhibitors,
  useRemoveExhibitor,
} from '@/hooks/use-event-exhibitors'
import { useWorkspace } from '@/lib/workspace-context'

export function EventExhibitors({
  eventId,
  tenantSlug,
  eventSlug,
}: {
  eventId: string
  tenantSlug: string
  eventSlug: string
}) {
  const { isAdmin } = useWorkspace()
  const { data: exhibitors, isLoading } = useEventExhibitors(eventId)
  const removeExhibitor = useRemoveExhibitor()
  const { toast } = useToast()
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)
  const [qrDialogExhibitor, setQrDialogExhibitor] = useState<string | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const getScanUrl = (exhibitorSlug: string) =>
    `${window.location.origin}/s/${tenantSlug}/${eventSlug}/${exhibitorSlug}`

  const copyLink = async (exhibitorSlug: string, id: string) => {
    await navigator.clipboard.writeText(getScanUrl(exhibitorSlug))
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const handleRemove = async (id: string) => {
    try {
      await removeExhibitor.mutateAsync({ id, eventId })
      toast({ title: 'Aussteller entfernt' })
    } catch {
      toast({ title: 'Fehler', variant: 'destructive' })
    }
  }

  if (isLoading) {
    return <Skeleton className="h-[200px] w-full" />
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Aussteller ({exhibitors?.length || 0})
            </CardTitle>
            <CardDescription>
              Aussteller, die an diesem Event teilnehmen
            </CardDescription>
          </div>
          {isAdmin && (
            <Button onClick={() => setAssignDialogOpen(true)}>
              <UserPlus className="h-4 w-4 mr-2" />
              Zuordnen
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {!exhibitors || exhibitors.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p className="text-sm">Noch keine Aussteller zugeordnet.</p>
              {isAdmin && (
                <p className="text-xs mt-1">
                  Klicke auf „Zuordnen", um Aussteller aus deinem Pool hinzuzufügen.
                </p>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Firma</TableHead>
                  <TableHead>Ansprechpartner</TableHead>
                  <TableHead>Scan-Seite</TableHead>
                  <TableHead>QR-Code</TableHead>
                  {isAdmin && <TableHead className="w-[50px]"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {exhibitors.map((ex) => (
                  <TableRow key={ex.id}>
                    <TableCell className="font-medium">{ex.companyName || ex.email}</TableCell>
                    <TableCell className="text-muted-foreground">{ex.name || '—'}</TableCell>
                    <TableCell>
                      {ex.profileSlug ? (
                        <a
                          href={getScanUrl(ex.profileSlug)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          Öffnen
                        </a>
                      ) : (
                        <Badge variant="outline" className="text-xs text-muted-foreground">
                          Profil fehlt
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {ex.profileSlug ? (
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setQrDialogExhibitor(ex.profileSlug!)}
                          >
                            <QrCode className="h-3.5 w-3.5 mr-1" />
                            QR
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => copyLink(ex.profileSlug!, ex.id)}
                          >
                            {copiedId === ex.id ? (
                              <Check className="h-3.5 w-3.5 text-green-600" />
                            ) : (
                              <Copy className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    {isAdmin && (
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemove(ex.id)}
                          disabled={removeExhibitor.isPending}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* QR Code Dialog */}
      {qrDialogExhibitor && (
        <ExhibitorQrDialog
          scanUrl={getScanUrl(qrDialogExhibitor)}
          exhibitorName={exhibitors?.find((e) => e.profileSlug === qrDialogExhibitor)?.companyName || exhibitors?.find((e) => e.profileSlug === qrDialogExhibitor)?.name || ''}
          open={!!qrDialogExhibitor}
          onOpenChange={(o) => { if (!o) setQrDialogExhibitor(null) }}
        />
      )}

      <AssignExhibitorsDialog
        eventId={eventId}
        open={assignDialogOpen}
        onOpenChange={setAssignDialogOpen}
      />
    </>
  )
}

function AssignExhibitorsDialog({
  eventId,
  open,
  onOpenChange,
}: {
  eventId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { data: available, isLoading } = useAvailableExhibitors(eventId)
  const bulkAssign = useBulkAssignExhibitors()
  const { toast } = useToast()
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const unassigned = available?.filter((a) => !a.alreadyAssigned) || []

  const toggleUser = (userId: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(userId)) {
        next.delete(userId)
      } else {
        next.add(userId)
      }
      return next
    })
  }

  const toggleAll = () => {
    if (selected.size === unassigned.length) {
      setSelected(new Set())
    } else {
      setSelected(new Set(unassigned.map((a) => a.userId)))
    }
  }

  const handleAssign = async () => {
    if (selected.size === 0) return

    try {
      await bulkAssign.mutateAsync({
        eventId,
        userIds: Array.from(selected),
      })
      toast({
        title: `${selected.size} Aussteller zugeordnet`,
      })
      setSelected(new Set())
      onOpenChange(false)
    } catch (err) {
      toast({
        title: 'Fehler',
        description: err instanceof Error ? err.message : 'Zuordnung fehlgeschlagen',
        variant: 'destructive',
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setSelected(new Set()); onOpenChange(o) }}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Aussteller zuordnen</DialogTitle>
          <DialogDescription>
            Wähle Aussteller aus deinem Pool, die an diesem Event teilnehmen.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto min-h-0">
          {isLoading ? (
            <div className="space-y-2 py-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : unassigned.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-sm">
                {available?.length === 0
                  ? 'Noch keine Aussteller im Pool. Lade zuerst Aussteller über die Aussteller-Seite ein.'
                  : 'Alle Aussteller sind bereits zugeordnet.'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]">
                    <Checkbox
                      checked={selected.size === unassigned.length && unassigned.length > 0}
                      onCheckedChange={toggleAll}
                    />
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>E-Mail</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {unassigned.map((ex) => (
                  <TableRow key={ex.userId}>
                    <TableCell>
                      <Checkbox
                        checked={selected.has(ex.userId)}
                        onCheckedChange={() => toggleUser(ex.userId)}
                      />
                    </TableCell>
                    <TableCell className="font-medium">{ex.name || '—'}</TableCell>
                    <TableCell>{ex.email}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* Bereits zugeordnete anzeigen */}
          {available && available.filter((a) => a.alreadyAssigned).length > 0 && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-xs text-muted-foreground mb-2">Bereits zugeordnet:</p>
              <div className="flex flex-wrap gap-1">
                {available
                  .filter((a) => a.alreadyAssigned)
                  .map((a) => (
                    <Badge key={a.userId} variant="secondary" className="text-xs">
                      {a.name || a.email}
                    </Badge>
                  ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button
            onClick={handleAssign}
            disabled={selected.size === 0 || bulkAssign.isPending}
          >
            {bulkAssign.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Wird zugeordnet...
              </>
            ) : (
              `${selected.size} zuordnen`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ExhibitorQrDialog({
  scanUrl,
  exhibitorName,
  open,
  onOpenChange,
}: {
  scanUrl: string
  exhibitorName: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [qrDataUrl, setQrDataUrl] = useState('')

  useEffect(() => {
    if (open && scanUrl) {
      QRCode.toDataURL(scanUrl, { width: 400, margin: 2 }).then(setQrDataUrl)
    }
  }, [open, scanUrl])

  const downloadQR = async () => {
    const svg = await QRCode.toString(scanUrl, { type: 'svg' })
    const blob = new Blob([svg], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.download = `qr-${exhibitorName.toLowerCase().replace(/\s+/g, '-')}.svg`
    link.href = url
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>QR-Code: {exhibitorName}</DialogTitle>
          <DialogDescription>
            Dieser QR-Code führt Besucher zur digitalen Messetasche.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4 py-4">
          {qrDataUrl && (
            <img src={qrDataUrl} alt={`QR für ${exhibitorName}`} className="w-64 h-64 rounded-lg" />
          )}
          <p className="text-xs text-muted-foreground text-center font-mono break-all px-4">
            {scanUrl}
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Schließen
          </Button>
          <Button onClick={downloadQR}>
            <Download className="h-4 w-4 mr-2" />
            SVG herunterladen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
